import toast from 'react-hot-toast';

/**
 * Скачивание или шаринг медиа (фото/видео).
 * На мобильных сначала открывает меню «Поделиться» (Сохранить в Фото и т.д.),
 * при отказе — открывает в новой вкладке (долгое нажатие → сохранить).
 * На десктопе — скачивание по ссылке.
 *
 * @param {string} url — URL медиафайла
 * @param {object} options
 * @param {string} [options.toastId='download'] — id для toast
 * @param {function(boolean)} [options.onLoadingChange] — вызывается с true в начале, с false по завершении
 * @param {object} [options.axios] — если передан, при ошибке fetch будет повтор через axios
 */
export async function downloadMedia(url, options = {}) {
  const { toastId = 'download', onLoadingChange, axios: axiosInstance } = options;

  const done = (loading = false) => {
    if (typeof onLoadingChange === 'function') onLoadingChange(loading);
  };

  done(true);
  toast.loading('Подготовка файла...', { id: toastId });

  const urlLower = (url || '').toLowerCase().split('?')[0];
  const byExtension = [
    ['.mp3', 'mp3', 'audio/mpeg'],
    ['.wav', 'wav', 'audio/wav'],
    ['.m4a', 'm4a', 'audio/mp4'],
    ['.ogg', 'ogg', 'audio/ogg'],
    ['.aac', 'aac', 'audio/aac'],
    ['.flac', 'flac', 'audio/flac'],
    ['.mp4', 'mp4', 'video/mp4'],
    ['.webm', 'webm', 'video/webm'],
    ['.mov', 'mov', 'video/quicktime'],
    ['.gif', 'gif', 'image/gif'],
    ['.webp', 'webp', 'image/webp'],
    ['.png', 'png', 'image/png'],
    ['.jpeg', 'jpg', 'image/jpeg'],
    ['.jpg', 'jpg', 'image/jpeg'],
  ];
  let extension = options.extension || 'jpg';
  let mimeType = options.mimeType || 'image/jpeg';
  if (!options.extension) {
    const match = byExtension.find(([ext]) => urlLower.endsWith(ext));
    if (match) {
      extension = match[1];
      mimeType = match[2];
    }
  }

  const filename = options.filename || `pikly-${Date.now()}.${extension}`;
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const isVideo = extension === 'mp4' || extension === 'webm' || extension === 'mov';

  const tryShareOrDownload = (blob) => {
    // На мобильных: Web Share API — меню «Поделиться» с опцией «Сохранить в Фото»
    if (isMobile && navigator.share) {
      try {
        const file = new File([blob], filename, { type: mimeType });
        const shareData = { files: [file] };
        if (navigator.canShare && navigator.canShare(shareData)) {
          return navigator.share(shareData);
        }
        return navigator.share(shareData);
      } catch (shareError) {
        if (shareError?.name !== 'AbortError') {
          console.log('Web Share недоступен или отменён, используем fallback:', shareError);
        }
        return Promise.reject(shareError);
      }
    }
    return Promise.reject(new Error('not_mobile_share'));
  };

  const doShareSuccess = () => {
    toast.dismiss(toastId);
    toast.success('Успешно сохранено');
    done(false);
  };

  const doFallbackNewTab = () => {
    const newWindow = window.open(url, '_blank');
    if (newWindow) {
      toast.dismiss(toastId);
      toast.success(
        isVideo
          ? 'Откройте видео, долго нажмите и выберите «Сохранить видео» для сохранения в Фото'
          : 'Откройте изображение и долго нажмите для сохранения в фотогалерею',
        { duration: 5000 }
      );
      done(false);
      return true;
    }
    return false;
  };

  const doDownloadLink = (blob) => {
    const link = document.createElement('a');
    const blobUrl = URL.createObjectURL(blob);
    link.href = blobUrl;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    toast.dismiss(toastId);
    toast.success('Файл скачивается');
    done(false);
  };

  try {
    toast.loading('Загрузка файла...', { id: toastId });
    const response = await fetch(url, { method: 'GET', headers: { Accept: '*/*' } });
    if (!response.ok) throw new Error('Failed to fetch');
    const headerType = (response.headers.get('content-type') || '').split(';')[0].trim().toLowerCase();
    if (!options.mimeType && headerType.startsWith('audio/')) {
      mimeType = headerType;
      if (headerType.includes('mpeg') || headerType.includes('mp3')) extension = 'mp3';
      else if (headerType.includes('wav')) extension = 'wav';
      else if (headerType.includes('mp4')) extension = 'm4a';
      else if (headerType.includes('ogg')) extension = 'ogg';
    }
    const blob = await response.blob();

    try {
      await tryShareOrDownload(blob);
      doShareSuccess();
      return;
    } catch (_) {
      // share отменён или недоступен
    }

    if (isMobile && doFallbackNewTab()) return;

    doDownloadLink(blob);
  } catch (fetchError) {
    console.error('Fetch error:', fetchError);
    if (axiosInstance && typeof axiosInstance.get === 'function') {
      try {
        toast.loading('Повторная попытка скачивания...', { id: toastId });
        const response = await axiosInstance.get(url, { responseType: 'blob' });
        const blob = new Blob([response.data], { type: mimeType });

        try {
          await tryShareOrDownload(blob);
          doShareSuccess();
          return;
        } catch (_) {}

        if (isMobile && doFallbackNewTab()) return;

        doDownloadLink(blob);
      } catch (axiosError) {
        console.error('Axios error:', axiosError);
        toast.dismiss(toastId);
        toast.error('Ошибка скачивания');
        done(false);
      }
    } else {
      toast.dismiss(toastId);
      toast.error('Ошибка скачивания');
      done(false);
    }
  }
}
